"use client"

import { useState, useEffect, useRef } from "react"
import { useSearchParams } from "next/navigation"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { ArrowLeft, Mail, AlertCircle, CheckCircle, Smartphone, ExternalLink } from "lucide-react"
import { validateNBCUser, type NBCUser } from "@/lib/services/nbc-api"
import { validateDepositToken, validateWithdrawalToken } from "@/lib/services/partners-api"
import { useLanguage } from "@/contexts/language-context"
import { LanguageSelector } from "@/components/mobile/language-selector"

interface EmailVerificationScreenProps {
  currentEmail?: string
  onEmailVerified: (email: string, user?: NBCUser, orderData?: OrderData) => void
  onBack?: () => void
  mode?: "deposit" | "withdraw" // Added mode prop to distinguish between deposit and withdraw
  token?: string // Token from URL params for secure flow
  orderId?: string // Order ID from URL params for validation
  initialOrderData?: OrderData // Pre-cached order data to avoid re-validation
  tokenValidatedRef?: React.MutableRefObject<string | null> // Ref del padre para persistencia entre re-renders
}

interface OrderData {
  order_id: string
  email: string
  amount: string
  payment_address: string
  expires_at: string
  partner_name: string
}

export function EmailVerificationScreen({
  currentEmail,
  onEmailVerified,
  onBack,
  mode = "deposit",
  token,
  orderId,
  initialOrderData,
  tokenValidatedRef: parentTokenValidatedRef,
}: EmailVerificationScreenProps) {
  const searchParams = useSearchParams()
  const [nbcEmail, setNbcEmail] = useState("")
  const [isVerifying, setIsVerifying] = useState(false)
  const [error, setError] = useState("")
  const [validationResult, setValidationResult] = useState<NBCUser | null>(null)
  const [notFoundEmail, setNotFoundEmail] = useState<string>("")
  const [showDownloadModal, setShowDownloadModal] = useState(false)
  const [orderData, setOrderData] = useState<OrderData | null>(initialOrderData || null)
  const [isTokenMode, setIsTokenMode] = useState(!!initialOrderData)
  const [isLoadingToken, setIsLoadingToken] = useState(false)
  const { t } = useLanguage()

  // Usar ref del padre si se proporciona (persiste entre re-renders del componente)
  // Si no, crear ref local (comportamiento anterior)
  const localTokenValidatedRef = useRef<string | null>(initialOrderData ? 'cached' : null)
  const tokenValidatedRef = parentTokenValidatedRef || localTokenValidatedRef

  // Get token and order_id from URL params
  const urlToken = searchParams?.get('token') || token
  const urlOrderId = searchParams?.get('order_id') || orderId

  // Auto-validate token and prefill email if token mode - solo se ejecuta una vez
  useEffect(() => {
    // Si ya tenemos initialOrderData, saltar validación
    if (initialOrderData) {
      console.log('✅ Using cached order data, skipping token validation')
      setNbcEmail(initialOrderData.email)
      return
    }
    
    if (urlToken && urlOrderId && tokenValidatedRef.current !== urlToken) {
      validateTokenAndPrefill()
    }
  }, []) // Sin dependencias para evitar múltiples llamadas

  const validateTokenAndPrefill = async () => {
    if (!urlToken || !urlOrderId) return
    
    // Si ya tenemos datos cacheados, no validar de nuevo
    if (initialOrderData) {
      console.log('✅ Cached order data exists, skipping validation')
      return
    }
    
    // Evitar llamadas duplicadas - verificación más robusta
    if (tokenValidatedRef.current === urlToken) {
      console.log('🔄 Token already validated, skipping...', { current: tokenValidatedRef.current, urlToken })
      return
    }
    
    // Si ya está cargando, no hacer otra llamada
    if (isLoadingToken) {
      console.log('⏳ Token validation already in progress, skipping...')
      return
    }
    
    // Marcar como en proceso ANTES de iniciar la llamada
    tokenValidatedRef.current = urlToken
    setIsLoadingToken(true)
    setIsTokenMode(true)
    setError("")

    try {
      console.log(`🔍 Validating ${mode} token:`, { token: urlToken.substring(0, 8) + '...', order_id: urlOrderId.substring(0, 8) + '...' })
      
      // La API key se maneja de forma segura en el servidor (proxy)
      // Usar función correcta según el modo (deposit o withdraw)
      const result = mode === 'deposit' 
        ? await validateDepositToken({ token: urlToken, order_id: urlOrderId })
        : await validateWithdrawalToken({ token: urlToken, order_id: urlOrderId })

      if (result.success && result.order_data) {
        console.log('✅ Token validated successfully:', result.order_data)
          
        const orderData = result.order_data
        setOrderData(orderData)
        setNbcEmail(orderData.email)
        
        // Auto-verify the pre-filled email, passing orderData explicitly
        setTimeout(() => {
          console.log('🔄 [validateTokenAndPrefill] Calling handleVerifyEmail with orderData:', orderData)
          handleVerifyEmail(orderData.email, orderData)
        }, 500)
        
      } else {
        console.error('❌ Token validation failed:', result.error)
        setError(result.error || t('email_verification.token_validation_error'))
        setIsTokenMode(false)
        tokenValidatedRef.current = null // Reset para permitir retry si es necesario
      }
    } catch (err) {
      console.error('❌ Error validating token:', err)
      setError(err instanceof Error ? err.message : t('error.network_error'))
      setIsTokenMode(false)
      tokenValidatedRef.current = null // Reset para permitir retry si es necesario
    } finally {
      setIsLoadingToken(false)
    }
  }

  const handleVerifyEmail = async (prefilledEmail?: string, prefilledOrderData?: any) => {
    const emailToVerify = prefilledEmail || nbcEmail
    const orderDataToUse = prefilledOrderData !== undefined ? prefilledOrderData : orderData
    
    console.log('🎯 [EMAIL-VERIFICATION] handleVerifyEmail called!')
    console.log('  ↳ emailToVerify:', emailToVerify)
    console.log('  ↳ prefilledEmail:', prefilledEmail)
    console.log('  ↳ nbcEmail:', nbcEmail)
    console.log('  ↳ prefilledOrderData:', prefilledOrderData)
    console.log('  ↳ orderDataToUse:', orderDataToUse)
    
    if (!emailToVerify || !emailToVerify.includes("@")) {
      console.log('❌ [EMAIL-VERIFICATION] Email validation failed')
      setError(t('email_verification.invalid_email'))
      return
    }

    setIsVerifying(true)
    setError("")
    setValidationResult(null)
    setNotFoundEmail("")

    try {
      console.log('🔍 Verifying NBC email:', emailToVerify)
      
      // Usar el servicio real de NBC API
      const result = await validateNBCUser(emailToVerify)
      
      console.log('✅ NBC validation result:', result)
      console.log('  ↳ result.success:', result.success)
      console.log('  ↳ orderDataToUse:', orderDataToUse)
      
      setValidationResult(result)

      if (result.success) {
        console.log('🎉 [EMAIL-VERIFICATION] Calling onEmailVerified callback!')
        console.log('  ↳ email:', emailToVerify)
        console.log('  ↳ user:', result)
        console.log('  ↳ orderDataToUse:', orderDataToUse || undefined)
        
        // Pasar los datos al componente padre para que maneje la actualización de la orden
        // El componente padre (deposit/page.tsx) se encargará de llamar a confirmEmailVerified()
        onEmailVerified(emailToVerify, result, orderDataToUse || undefined)
        
        console.log('✅ [EMAIL-VERIFICATION] onEmailVerified callback executed!')
      } else {
        console.log('❌ [EMAIL-VERIFICATION] result.success is false')
        setError(result.message || t('email_verification.validation_error'))
      }

    } catch (err) {
      console.error('❌ NBC validation error:', err)
      
      // Detectar específicamente HTTP 404 para mostrar email not found
      if (err instanceof Error && err.message.includes('HTTP 404')) {
        setNotFoundEmail(emailToVerify)
        setError(t('email_verification.email_not_registered', { email: emailToVerify }))
      } else {
        setError(err instanceof Error ? err.message : t('error.network_error'))
      }
      setValidationResult(null)
    } finally {
      setIsVerifying(false)
    }
  }

  const handleVerifyButtonClick = () => {
    handleVerifyEmail()
  }

  return (
    <div className="max-w-md mx-auto min-h-screen bg-background">
      <div className="sticky top-0 bg-background/95 backdrop-blur-sm border-b border-border p-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} disabled={isVerifying}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-3 flex-1">
            <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-sm">N</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-foreground tracking-tight">NBC TALK PAY</h1>
              <p className="text-xs text-muted-foreground font-medium">{t('email_verification.title')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSelector />
            {isVerifying && (
              <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
            )}
          </div>
        </div>
      </div>

      <div className="p-4 space-y-6">
        <div className="animate-in slide-in-from-right-4 duration-300">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                {t('email_verification.title')}
              </CardTitle>
              <CardDescription>{t('email_verification.description', { mode })}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoadingToken && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mt-0.5" />
                    <div className="text-sm">
                      <p className="text-blue-800 font-medium">{t('email_verification.token_validation_title')}</p>
                      <p className="text-blue-700 mt-1">{t('email_verification.token_validation_description', { mode })}</p>
                    </div>
                  </div>
                </div>
              )}

              {orderData && isTokenMode && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="text-green-800 font-medium">{t('email_verification.order_confirmed_title', { mode })}</p>
                      <p className="text-green-700 mt-1">
                        {orderData.amount ? t('email_verification.order_confirmed_amount', { amount: orderData.amount }) + ' • ' : ''}{t('email_verification.order_confirmed_id', { orderId: orderData.order_id.slice(0, 8) + '...' })}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {notFoundEmail && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-orange-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="text-orange-800 font-medium">{t('email_verification.email_not_found')}</p>
                      <p className="text-orange-700 mt-1">
                        {t('email_verification.email_not_registered', { email: notFoundEmail })}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="nbc-email">{t('email_verification.email_label')}</Label>
                <Input
                  id="nbc-email"
                  type="email"
                  placeholder={t('email_verification.email_placeholder')}
                  value={nbcEmail}
                  onChange={(e) => {
                    setNbcEmail(e.target.value)
                    setError("")
                    setNotFoundEmail("")
                  }}
                  className="transition-all duration-200 focus:scale-[1.02]"
                  disabled={isVerifying}
                />
                <p className="text-xs text-muted-foreground">
                  {t('email_verification.email_help')}
                </p>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg animate-in slide-in-from-top-2 duration-300">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              )}

              <Button
                className="w-full transition-all duration-200 hover:scale-[1.02]"
                onClick={handleVerifyButtonClick}
                disabled={!nbcEmail || isVerifying}
              >
                {isVerifying ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('email_verification.verifying')}
                  </div>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {t('email_verification.verify_button')}
                  </>
                )}
              </Button>

              <Button
                variant="outline"
                className="w-full bg-transparent transition-all duration-200 hover:scale-[1.02]"
                onClick={() => setShowDownloadModal(true)}
              >
                <Smartphone className="h-4 w-4 mr-2" />
                {t('email_verification.download_app')}
              </Button>
            </CardContent>
          </Card>

          <Card className="mt-4">
            <CardContent className="pt-6">
              <div className="text-center space-y-2">
                <Mail className="h-8 w-8 text-primary mx-auto" />
                <h3 className="font-medium">{t('email_verification.why_needed_title')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('email_verification.why_needed_description')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="p-4 border-t border-border bg-muted/30">
        <div className="text-center">
          <p className="text-xs text-muted-foreground/70">
            {t('common.copyright')} <span className="font-semibold text-muted-foreground">NBC BANQ</span>
          </p>
        </div>
      </div>

      {/* Modal de descarga de NBC TALK PAY */}
      <Dialog open={showDownloadModal} onOpenChange={setShowDownloadModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center flex items-center justify-center gap-2">
              <Smartphone className="h-6 w-6 text-purple-600" />
              {t('email_verification.download_modal_title')}
            </DialogTitle>
            <DialogDescription className="text-center">
              {t('email_verification.download_modal_description')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col gap-4 py-4">
            {/* App Store Button */}
            <Button
              variant="outline"
              className="w-full h-16 flex items-center justify-start gap-4 p-4 hover:bg-gray-50 transition-colors"
              onClick={() => window.open("https://apps.apple.com/am/app/nbc-talk/id1553055704", "_blank")}
            >
              <div className="flex items-center justify-center w-12 h-12 bg-black rounded-lg text-white">
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-900">{t('email_verification.app_store')}</div>
                <div className="text-sm text-gray-500">{t('email_verification.download_ios')}</div>
              </div>
              <ExternalLink className="h-4 w-4 ml-auto text-gray-400" />
            </Button>

            {/* Play Store Button */}
            <Button
              variant="outline"
              className="w-full h-16 flex items-center justify-start gap-4 p-4 hover:bg-gray-50 transition-colors"
              onClick={() => window.open("https://play.google.com/store/apps/details?id=com.beint.nbctalk.com&hl=es_419", "_blank")}
            >
              <div className="flex items-center justify-center w-12 h-12 bg-linear-to-br from-green-400 via-blue-500 to-purple-600 rounded-lg text-white">
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-900">{t('email_verification.play_store')}</div>
                <div className="text-sm text-gray-500">{t('email_verification.download_android')}</div>
              </div>
              <ExternalLink className="h-4 w-4 ml-auto text-gray-400" />
            </Button>
          </div>

          <div className="text-center pt-2">
            <p className="text-xs text-gray-500">
              {t('email_verification.app_required')}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
